import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import BlockIcon from "@mui/icons-material/Block";
import DeleteIcon from "@mui/icons-material/Delete";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import RefreshIcon from "@mui/icons-material/Refresh";

// test 
const ChatGuestsSection = () => {
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterBanned, setFilterBanned] = useState("all");
  const [filterClaimed, setFilterClaimed] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [claimedCount, setClaimedCount] = useState(0);

  // Confirmation modal state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    action: null,
    guest: null,
    showDeleteMessages: false,
    deleteMessages: false,
  });

  useEffect(() => {
    fetchGuests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filterBanned, filterClaimed]);

  const fetchGuests = async (search = searchTerm) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page,
        limit: 50,
        search,
      });
      if (filterBanned !== "all") {
        params.append("banned", filterBanned);
      }
      if (filterClaimed !== "all") {
        params.append("claimed", filterClaimed);
      }
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/chatguests?${params}`
      );
      setGuests(response.data.guests);
      setTotalPages(response.data.totalPages);
      setTotal(response.data.total);
      if (response.data.claimedCount !== undefined) {
        setClaimedCount(response.data.claimedCount);
      }
    } catch (err) {
      console.error("Failed to fetch guests:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchGuests(searchTerm);
  };

  const openConfirmDialog = (title, message, action, guest, showDeleteMessages = false) => {
    setConfirmDialog({
      open: true,
      title,
      message,
      action,
      guest,
      showDeleteMessages,
      deleteMessages: false,
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({
      open: false,
      title: "",
      message: "",
      action: null,
      guest: null,
      showDeleteMessages: false,
      deleteMessages: false,
    });
  };

  const handleConfirmAction = async () => {
    const { action, guest, deleteMessages } = confirmDialog;
    closeConfirmDialog();

    if (action === "ban") {
      try {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/admin/chatguests/${guest.id}/ban`,
          { is_banned: true, ban_reason: "Banned by admin" }
        );
        fetchGuests();
      } catch (err) {
        console.error("Failed to ban guest:", err);
      }
    } else if (action === "unban") {
      try {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/admin/chatguests/${guest.id}/ban`,
          { is_banned: false, ban_reason: null }
        );
        fetchGuests();
      } catch (err) {
        console.error("Failed to unban guest:", err);
      }
    } else if (action === "delete") {
      try {
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/api/admin/chatguests/${guest.id}?deleteMessages=${deleteMessages}`
        );
        fetchGuests();
      } catch (err) {
        console.error("Failed to delete guest:", err);
      }
    }
  };

  const handleBanToggle = (guest) => {
    if (guest.is_banned) {
      openConfirmDialog(
        "Unban User",
        `Are you sure you want to unban "${guest.username}"?`,
        "unban",
        guest
      );
    } else {
      openConfirmDialog(
        "Ban User",
        `Are you sure you want to ban "${guest.username}"? They will no longer be able to access the chatroom.`,
        "ban",
        guest
      );
    }
  };

  const handleDelete = (guest) => {
    openConfirmDialog(
      "Delete User",
      `Are you sure you want to permanently delete "${guest.username}"? This action cannot be undone.`,
      "delete",
      guest,
      true // show delete messages option
    );
  };

  const getAge = (birthYear) => {
    if (!birthYear) return "-";
    return new Date().getFullYear() - birthYear;
  };

  if (loading && guests.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress sx={{ color: "#fff" }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Stats */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 1.5, backgroundColor: "#16213e", borderRadius: 1, border: "1px solid #394867", textAlign: "center" }}>
            <Typography variant="h5" sx={{ color: "#4caf50", fontWeight: 700 }}>{total}</Typography>
            <Typography variant="caption" sx={{ color: "#9e9e9e" }}>Total Guests</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 1.5, backgroundColor: "#16213e", borderRadius: 1, border: "1px solid #394867", textAlign: "center" }}>
            <Typography variant="h5" sx={{ color: "#1565c0", fontWeight: 700 }}>{claimedCount}</Typography>
            <Typography variant="caption" sx={{ color: "#9e9e9e" }}>Claimed Accounts</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6} sx={{ display: "flex", alignItems: "center", justifyContent: { xs: "center", md: "flex-end" } }}>
          <Button
            component={Link}
            to="/admin/chatroom/guests/fingerprinting"
            variant="outlined"
            startIcon={<FingerprintIcon />}
            sx={{
              color: "#ff9800",
              borderColor: "#ff9800",
              "&:hover": { borderColor: "#ffb74d", backgroundColor: "rgba(255, 152, 0, 0.1)" },
            }}
          >
            Fingerprinting Analytics
          </Button>
        </Grid>
      </Grid>

      {/* Search and Filter */}
      <Paper sx={{ p: 2, mb: 2, backgroundColor: "#16213e", borderRadius: 2, border: "1px solid #394867" }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by username or city..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#9e9e9e" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  color: "#fff",
                  "& fieldset": { borderColor: "#394867" },
                  "&:hover fieldset": { borderColor: "#541b14" },
                  "&.Mui-focused fieldset": { borderColor: "#541b14" },
                },
              }}
            />
          </Grid>
          <Grid item xs={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: "#9e9e9e" }}>Status</InputLabel>
              <Select
                value={filterBanned}
                label="Status"
                onChange={(e) => { setFilterBanned(e.target.value); setPage(1); }}
                sx={{
                  color: "#fff",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#394867" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#541b14" },
                  "& .MuiSvgIcon-root": { color: "#9e9e9e" },
                }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="false">Active</MenuItem>
                <MenuItem value="true">Banned</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: "#9e9e9e" }}>Claimed</InputLabel>
              <Select
                value={filterClaimed}
                label="Claimed"
                onChange={(e) => { setFilterClaimed(e.target.value); setPage(1); }}
                sx={{
                  color: "#fff",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#394867" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#541b14" },
                  "& .MuiSvgIcon-root": { color: "#9e9e9e" },
                }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="true">Claimed</MenuItem>
                <MenuItem value="false">Unclaimed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={4} md={2} sx={{ display: "flex", gap: 1 }}>
            <Button variant="outlined" onClick={handleSearch} sx={{ color: "#fff", borderColor: "#394867" }}>
              Search
            </Button>
            <IconButton
              onClick={() => fetchGuests()}
              disabled={loading}
              sx={{ color: "#9e9e9e", "&:hover": { color: "#fff", backgroundColor: "#394867" } }}
              title="Refresh"
            >
              <RefreshIcon sx={{ animation: loading ? "spin 1s linear infinite" : "none", "@keyframes spin": { "0%": { transform: "rotate(0deg)" }, "100%": { transform: "rotate(360deg)" } } }} />
            </IconButton>
          </Grid>
        </Grid>
      </Paper>

      {/* Guests Table */}
      <Paper sx={{ backgroundColor: "#16213e", borderRadius: 2, border: "1px solid #394867", overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: 500 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", fontWeight: 600, borderBottom: "1px solid #394867", py: 1 }}>Username</TableCell>
                <TableCell sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", fontWeight: 600, borderBottom: "1px solid #394867", py: 1 }}>Gender</TableCell>
                <TableCell sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", fontWeight: 600, borderBottom: "1px solid #394867", py: 1 }}>Age</TableCell>
                <TableCell sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", fontWeight: 600, borderBottom: "1px solid #394867", py: 1 }}>Location</TableCell>
                <TableCell sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", fontWeight: 600, borderBottom: "1px solid #394867", py: 1 }}>Messages</TableCell>
                <TableCell sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", fontWeight: 600, borderBottom: "1px solid #394867", py: 1 }}>Last Seen</TableCell>
                <TableCell sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", fontWeight: 600, borderBottom: "1px solid #394867", py: 1 }}>Status</TableCell>
                <TableCell align="center" sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", fontWeight: 600, borderBottom: "1px solid #394867", py: 1 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {guests.map((guest) => (
                <TableRow key={guest.id} hover sx={{ "&:hover": { backgroundColor: "#1a1a2e" }, opacity: guest.is_banned ? 0.5 : 1 }}>
                  <TableCell sx={{ color: "#fff", borderBottom: "1px solid #394867", py: 0.75, fontWeight: 500 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <a
                        href={`/admin/chatroom/user/${guest.username}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#64b5f6",
                          textDecoration: "none",
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                        onMouseEnter={(e) => e.target.style.textDecoration = "underline"}
                        onMouseLeave={(e) => e.target.style.textDecoration = "none"}
                      >
                        {guest.username}
                        <OpenInNewIcon sx={{ fontSize: 12, opacity: 0.7 }} />
                      </a>
                      {guest.is_claimed && (
                        <Chip
                          label="Claimed"
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: "0.65rem",
                            backgroundColor: "#1565c0",
                            color: "#fff",
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867", py: 0.75, textTransform: "capitalize" }}>
                    {guest.gender || "-"}
                  </TableCell>
                  <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867", py: 0.75 }}>
                    {getAge(guest.birth_year)}
                  </TableCell>
                  <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867", py: 0.75 }}>
                    {guest.location_city || guest.location_zip || "-"}
                  </TableCell>
                  <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867", py: 0.75 }}>
                    {guest.message_count || 0}
                  </TableCell>
                  <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867", py: 0.75, fontSize: "0.75rem" }}>
                    {guest.last_seen_at ? new Date(guest.last_seen_at).toLocaleDateString() : "-"}
                  </TableCell>
                  <TableCell sx={{ borderBottom: "1px solid #394867", py: 0.75 }}>
                    <Chip
                      label={guest.is_banned ? "Banned" : "Active"}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.7rem",
                        backgroundColor: guest.is_banned ? "#b71c1c" : "#1b5e20",
                        color: "#fff",
                      }}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ borderBottom: "1px solid #394867", py: 0.75 }}>
                    <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleBanToggle(guest)}
                        sx={{ color: guest.is_banned ? "#4caf50" : "#ef5350", p: 0.5 }}
                        title={guest.is_banned ? "Unban" : "Ban"}
                      >
                        <BlockIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(guest)}
                        sx={{ color: "#ff5722", p: 0.5 }}
                        title="Delete User"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {guests.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} sx={{ color: "#666", textAlign: "center", py: 4, borderBottom: "1px solid #394867" }}>
                    No guests found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 2, p: 2, borderTop: "1px solid #394867" }}>
            <Button
              size="small"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              sx={{ color: "#fff" }}
            >
              Previous
            </Button>
            <Typography variant="body2" sx={{ color: "#9e9e9e" }}>
              Page {page} of {totalPages}
            </Typography>
            <Button
              size="small"
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
              sx={{ color: "#fff" }}
            >
              Next
            </Button>
          </Box>
        )}
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={closeConfirmDialog}
        PaperProps={{
          sx: {
            backgroundColor: "#1a1a2e",
            border: "1px solid #394867",
            borderRadius: 2,
            minWidth: 400,
          },
        }}
      >
        <DialogTitle sx={{ color: "#fff", borderBottom: "1px solid #394867" }}>
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <DialogContentText sx={{ color: "#9e9e9e" }}>
            {confirmDialog.message}
          </DialogContentText>
          {confirmDialog.showDeleteMessages && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: "#16213e", borderRadius: 1, border: "1px solid #394867" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={confirmDialog.deleteMessages}
                    onChange={(e) =>
                      setConfirmDialog((prev) => ({
                        ...prev,
                        deleteMessages: e.target.checked,
                      }))
                    }
                    sx={{
                      color: "#9e9e9e",
                      "&.Mui-checked": { color: "#ef5350" },
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ color: "#fff" }}>
                      Also delete all messages
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#9e9e9e" }}>
                      If unchecked, messages will be anonymized (guest_id set to null)
                    </Typography>
                  </Box>
                }
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid #394867" }}>
          <Button onClick={closeConfirmDialog} sx={{ color: "#9e9e9e" }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            variant="contained"
            sx={{
              backgroundColor: confirmDialog.action === "unban" ? "#4caf50" : "#ef5350",
              "&:hover": {
                backgroundColor: confirmDialog.action === "unban" ? "#388e3c" : "#d32f2f",
              },
            }}
          >
            {confirmDialog.action === "ban" && "Ban User"}
            {confirmDialog.action === "unban" && "Unban User"}
            {confirmDialog.action === "delete" && "Delete User"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChatGuestsSection;
